import React from "react";
import { View, Text, StyleSheet, Platform, Dimensions } from "react-native";
import { useShowAds } from "../hooks/useShowAds";
// AdSenseBanner is a DOM component — only imported on web to avoid RN crashes
let AdSenseBanner: any = null;
let AD_SLOTS: any = null;
if (Platform.OS === "web") {
  const adsense = require("./AdSenseBanner");
  AdSenseBanner = adsense.default;
  AD_SLOTS = adsense.AD_SLOTS;
}

// ---------------------------------------------------------------------------
// Ad Unit IDs — create separate units per platform in AdMob console if needed
// ---------------------------------------------------------------------------
const AD_UNIT_IDS = {
  banner: {
    android: "ca-app-pub-7439240901787604/5744305798",
    ios: "ca-app-pub-7439240901787604/5744305798", // replace with iOS unit ID if different
  },
  rewarded: {
    android: "ca-app-pub-7439240901787604/6865815778",
    ios: "ca-app-pub-7439240901787604/6865815778", // replace with iOS unit ID if different
  },
};

// Lazy-load the native AdMob module only on iOS/Android to avoid web crashes
let BannerAd: any = null;
let BannerAdSize: any = null;
if (Platform.OS !== "web") {
  try {
    const admob = require("react-native-google-mobile-ads");
    BannerAd = admob.BannerAd;
    BannerAdSize = admob.BannerAdSize;
  } catch (_) {}
}

type AdBannerVariant = "feed" | "detail";

interface AdBannerProps {
  variant?: AdBannerVariant;
}

const { width: SCREEN_WIDTH } = Dimensions.get("window");

// Show the dev placeholder on localhost so ad slots are visible during development
const IS_DEV_HOST =
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  !!window.location &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

const AdBanner: React.FC<AdBannerProps> = ({ variant = "feed" }) => {
  const showAds = useShowAds();
  if (!showAds) return null;

  // ── Web / Desktop — Google AdSense ─────────────────────────────────────
  if (Platform.OS === "web") {
    const slot = AD_SLOTS?.[variant] ?? AD_SLOTS?.feed;
    return (
      <View
        style={[
          styles.webContainer,
          variant === "detail" && styles.webContainerDetail,
        ]}
      >
        <Text style={styles.webLabel}>Advertisement</Text>
        {AdSenseBanner && slot && !IS_DEV_HOST ? (
          <AdSenseBanner
            slot={slot}
            style={{ width: Math.min(SCREEN_WIDTH - 32, 728), minHeight: 90 }}
          />
        ) : (
          <View style={styles.webPlaceholder}>
            <Text style={styles.webPlaceholderText}>
              {IS_DEV_HOST ? "📢 Ad slot (AdSense loads on production)" : "Ad"}
            </Text>
          </View>
        )}
      </View>
    );
  }

  // ── iOS / Android — native AdMob banner ────────────────────────────────
  if (!BannerAd || !BannerAdSize) return null;

  const unitId =
    Platform.OS === "android"
      ? AD_UNIT_IDS.banner.android
      : AD_UNIT_IDS.banner.ios;

  return (
    <View
      style={[
        styles.nativeContainer,
        variant === "detail" && styles.nativeContainerDetail,
      ]}
    >
      <BannerAd
        unitId={unitId}
        size={BannerAdSize.BANNER}
        requestOptions={{ requestNonPersonalizedAdsOnly: false }}
        onAdFailedToLoad={(error: Error) =>
          console.warn("[AdBanner] failed to load:", error.message)
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  // ── Native ──────────────────────────────────────────────────────────────
  nativeContainer: {
    alignItems: "center",
    backgroundColor: "#f8f9fa",
    paddingVertical: 6,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderColor: "#e5e7eb",
    width: "100%",
  },
  nativeContainerDetail: {
    marginVertical: 8,
    borderRadius: 8,
    overflow: "hidden",
  },

  // ── Web / Desktop ───────────────────────────────────────────────────────
  webContainer: {
    alignItems: "center",
    paddingVertical: 8,
    backgroundColor: "#f8f9fa",
    borderTopWidth: StyleSheet.hairlineWidth,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderColor: "#e5e7eb",
    width: "100%",
  },
  webContainerDetail: {
    marginVertical: 8,
    borderRadius: 8,
    overflow: "hidden",
  },
  webLabel: {
    fontSize: 10,
    color: "#9ca3af",
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  webPlaceholder: {
    // Responsive: 728px leaderboard on wide screens, full-width banner on narrow
    width: Math.min(SCREEN_WIDTH - 32, 728),
    height: 90,
    backgroundColor: "#e5e7eb",
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  webPlaceholderText: {
    color: "#9ca3af",
    fontSize: 14,
  },
});

export default AdBanner;
